import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  input,
} from '@angular/core';
import { Router } from '@angular/router';
import { Card } from 'primeng/card';
import { Button } from 'primeng/button';
import { AsyncPipe, DatePipe, NgClass } from '@angular/common';
import { AnnouncementDetails } from '../../../../../shared/models/announcement-details';
import {
  ANNOUNCEMENT_STATUS_LABELS,
  AnnouncementStatus,
} from '../../../../../shared/enums/announcement-status.enum';
import {
  ANNOUNCEMENT_CHANNEL_LABELS,
  AnnouncementChannel,
} from '../../../../../shared/enums/announcement-channel.enum';
import {
  ANNOUNCEMENT_RECIPIENT_TYPE_LABELS,
  AnnouncementRecipientType,
} from '../../../../../shared/enums/announcement-recipient-type.enum';
import { DialogService } from 'primeng/dynamicdialog';
import { AnnouncementReadStatusDialog } from '../announcement-read-status-dialog/announcement-read-status-dialog';
import { APP_PATHS } from '../../../../../core/config/app-paths';
import {
  ANNOUNCEMENT_CONFIRMATION_REQUIREMENT_LABELS,
  AnnouncementConfirmationRequirement,
} from '../../../../../shared/enums/announcement-confirmation-requirement.enum';
import { SafeHtmlPipe } from '../../../../../shared/pipes/safe-html-pipe';
import { TextResourcePipe } from '../../../../../shared/pipes/text-resource.pipe';
import { ConfirmDialogComponent } from '../../../../../shared/components/confirm-dialog/confirm-dialog.component';
import { catchError, EMPTY, filter, finalize, tap } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ToastSeverity } from '../../../../../shared/enums/toast-severity.enum';
import { SpinnerService } from '../../../../../core/services/spinner.service';
import {
  HttpResponseErrorSource,
  HttpResponseService,
} from '../../../../../core/services/http-error-response.service';
import { AnnouncementsApi } from '../../../../../shared/services/api/announcements.api';
import { MessageService } from 'primeng/api';
import { AnnouncementEmailStatsDialog } from '../announcement-email-stats-dialog/announcement-email-stats-dialog';
import { AnnouncementEmailStats } from '../../../../../shared/models/announcement-email-stats';
import { Tooltip } from 'primeng/tooltip';

@Component({
  selector: 'app-admin-panel-announcement-details',
  imports: [
    Card,
    Button,
    DatePipe,
    NgClass,
    AsyncPipe,
    SafeHtmlPipe,
    TextResourcePipe,
    Tooltip,
  ],
  templateUrl: './announcement-details.component.html',
  styleUrl: './announcement-details.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [],
})
export class AnnouncementDetailsComponent {
  announcement = input.required<AnnouncementDetails>();
  protected readonly AnnouncementStatus = AnnouncementStatus;
  readonly #dialogService = inject(DialogService);
  readonly #router = inject(Router);
  readonly #destroyRef = inject(DestroyRef);
  readonly #spinnerService = inject(SpinnerService);
  readonly #httpResponseService = inject(HttpResponseService);
  readonly #announcementsApi: AnnouncementsApi = inject(AnnouncementsApi);
  readonly #messageService = inject(MessageService);

  protected statusLabel(status: AnnouncementStatus): string {
    return ANNOUNCEMENT_STATUS_LABELS[status] ?? '-';
  }

  protected channelLabel(channel: AnnouncementChannel): string {
    return ANNOUNCEMENT_CHANNEL_LABELS[channel] ?? '-';
  }

  protected recipientTypeLabel(type: AnnouncementRecipientType): string {
    return ANNOUNCEMENT_RECIPIENT_TYPE_LABELS[type] ?? '-';
  }

  protected requiresConfirmationLabel(
    type: AnnouncementConfirmationRequirement,
  ): string {
    return ANNOUNCEMENT_CONFIRMATION_REQUIREMENT_LABELS[type] ?? '-';
  }

  protected statusPillClass(status: AnnouncementStatus): string {
    switch (status) {
      case AnnouncementStatus.ROBOCZY:
        return 'status-pill status-draft';
      case AnnouncementStatus.OPUBLIKOWANY:
        return 'status-pill status-published';
      case AnnouncementStatus.ARCHIWALNY:
        return 'status-pill status-archived';
      case AnnouncementStatus.BLAD:
        return 'status-pill status-error';
      default:
        return 'status-pill';
    }
  }

  protected shouldShowSystemFields(): boolean {
    const channel = this.announcement()?.channel;
    return (
      channel === AnnouncementChannel.SYSTEM ||
      channel === AnnouncementChannel.SYSTEM_I_EMAIL
    );
  }

  protected shouldShowEmailFields(): boolean {
    const channel = this.announcement()?.channel;
    return (
      channel === AnnouncementChannel.EMAIL ||
      channel === AnnouncementChannel.SYSTEM_I_EMAIL
    );
  }

  protected shouldShowRecipients(): boolean {
    return (
      this.announcement()?.recipientType !== AnnouncementRecipientType.WSZYSCY
    );
  }

  protected shouldShowRequestType(): boolean {
    return !!this.announcement()?.requestCode;
  }

  protected onBack(): void {
    this.#router.navigate([
      `/${APP_PATHS.admin.root}/${APP_PATHS.admin.announcements}`,
    ]);
  }

  protected onEdit(): void {
    if (!this.announcement()) {
      return;
    }

    this.#router.navigate([
      `/${APP_PATHS.admin.root}/${APP_PATHS.admin.announcements}/${this.announcement()!.id}/${APP_PATHS.admin.edit}`,
    ]);
  }

  protected onPublish(): void {
    if (!this.announcement()) {
      return;
    }

    if (
      (this.announcement().channel === AnnouncementChannel.EMAIL ||
        this.announcement().channel === AnnouncementChannel.SYSTEM_I_EMAIL) &&
      (this.announcement().recipientType ===
        AnnouncementRecipientType.WSZYSCY ||
        this.announcement().recipientType === AnnouncementRecipientType.WNIOSEK)
    ) {
      this.#onPublishWithEmail();
    } else {
      this.#showPublishAnnouncementDialog(ConfirmDialogComponent);
    }
  }

  protected onArchive(): void {
    if (!this.announcement()) {
      return;
    }

    const ref = this.#dialogService.open(ConfirmDialogComponent, {
      closable: true,
      header: 'Czy na pewno chcesz zarchiwizować komunikat?',
      closeOnEscape: true,
      modal: true,
      dismissableMask: true,
      inputValues: {
        confirmButtonLabel: 'Tak',
      },
    });

    ref?.onClose
      .pipe(
        filter(Boolean),
        tap(() => this.#archiveAnnouncement(this.announcement().id!)),
        takeUntilDestroyed(this.#destroyRef),
      )
      .subscribe();
  }

  protected onShowReadStatus(): void {
    if (!this.announcement()) {
      return;
    }

    this.#dialogService.open(AnnouncementReadStatusDialog, {
      header: 'Stan odczytania komunikatu przez użytkowników',
      closable: true,
      modal: true,
      closeOnEscape: true,
      dismissableMask: true,
      width: '1000px',
      breakpoints: {
        '960px': '90vw',
        '640px': '90vw',
      },
      inputValues: {
        announcementId: this.announcement()!.id,
        announcementChannel: this.announcement()!.channel,
      },
    });
  }

  #onPublishWithEmail(): void {
    this.#spinnerService.show();
    this.#announcementsApi
      .getAnnouncementEmailStats$(this.announcement().id!)
      .pipe(
        tap((emailStats) => {
          this.#showPublishAnnouncementDialog(
            AnnouncementEmailStatsDialog,
            emailStats,
          );
        }),
        catchError((err) => {
          this.#httpResponseService.showErrorMessage(
            err,
            'Wystąpił błąd podczas szacowania czasu wysyłki wiadomości e-mail.',
          );
          return EMPTY;
        }),
        finalize(() => this.#spinnerService.hide()),
        takeUntilDestroyed(this.#destroyRef),
      )
      .subscribe();
  }

  #showPublishAnnouncementDialog(
    dialogComponent: any,
    emailStats?: AnnouncementEmailStats,
  ): void {
    const ref = this.#dialogService.open(dialogComponent, {
      closable: true,
      header: 'Czy na pewno chcesz opublikować komunikat?',
      closeOnEscape: true,
      modal: true,
      dismissableMask: true,
      inputValues: {
        confirmButtonLabel: 'Tak',
        emailStats: emailStats || null,
      },
    });

    ref?.onClose
      .pipe(
        filter(Boolean),
        tap(() => this.#publishAnnouncement(this.announcement().id!)),
        takeUntilDestroyed(this.#destroyRef),
      )
      .subscribe();
  }

  #publishAnnouncement(announcementId: number): void {
    this.#spinnerService.show();
    this.#announcementsApi
      .publishAnnouncement$(announcementId)
      .pipe(
        tap(() => {
          this.#messageService.add({
            severity: ToastSeverity.SUCCESS,
            summary: 'Poprawnie opublikowano komunikat.',
          });
          this.#router.navigate([
            `/${APP_PATHS.admin.root}/${APP_PATHS.admin.announcements}`,
          ]);
        }),
        catchError((err) => {
          this.#httpResponseService.showErrorMessage(
            err,
            'Wystąpił błąd podczas próby opublikowania komunikatu.',
            HttpResponseErrorSource.MESSAGE_FROM_ERROR_CODE,
          );
          return EMPTY;
        }),
        finalize(() => this.#spinnerService.hide()),
        takeUntilDestroyed(this.#destroyRef),
      )
      .subscribe();
  }

  #archiveAnnouncement(announcementId: number): void {
    this.#spinnerService.show();
    this.#announcementsApi
      .archiveAnnouncement$(announcementId)
      .pipe(
        tap(() => {
          this.#messageService.add({
            severity: ToastSeverity.SUCCESS,
            summary: 'Poprawnie zarchiwizowano komunikat.',
          });
          this.#router.navigate([
            `/${APP_PATHS.admin.root}/${APP_PATHS.admin.announcements}`,
          ]);
        }),
        catchError((err) => {
          this.#httpResponseService.showErrorMessage(
            err,
            'Wystąpił błąd podczas próby archiwizacji komunikatu',
          );
          return EMPTY;
        }),
        finalize(() => this.#spinnerService.hide()),
        takeUntilDestroyed(this.#destroyRef),
      )
      .subscribe();
  }
}
